import type { Claim, ProblemRecord, Program } from "./types";

/** Deterministic pseudo-random generator so demo claims are stable across runs. */
function rng(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    return ((h >>> 0) % 100000) / 100000;
  };
}

const VIN_CHARS = "ABCDEFGHJKLMNPRSTUVWXYZ0123456789";

const WMI: Record<string, string> = {
  "T2 Body-on-Frame": "1BV",
  "E3 Skateboard": "5BV",
  "C4 Commercial": "3BV",
};

const YEAR_CODE: Record<number, string> = {
  2023: "P",
  2024: "R",
  2025: "S",
  2026: "T",
  2027: "V",
};

const DEALERS: { name: string; region: string }[] = [
  { name: "Northgate Motors — Dearborn, MI", region: "North America" },
  { name: "Lakeshore Auto Group — Chicago, IL", region: "North America" },
  { name: "Sunbelt Vehicle Centre — Phoenix, AZ", region: "North America" },
  { name: "Cascade Fleet Service — Portland, OR", region: "North America" },
  { name: "Maple Ridge Automotive — Toronto, ON", region: "North America" },
  { name: "Rhein Fahrzeugservice — Cologne", region: "Europe" },
  { name: "Nordvik Bil — Gothenburg", region: "Europe" },
  { name: "Midlands Commercial — Birmingham", region: "Europe" },
  { name: "Pacific Rim Motors — Osaka", region: "Asia Pacific" },
  { name: "Harbour City Vehicles — Sydney", region: "Asia Pacific" },
];

const STATUSES: Claim["status"][] = ["paid", "paid", "paid", "approved", "in-review", "rejected"];

const SYMPTOMS = [
  "Customer states warning lamp illuminated intermittently; fault reproduced during road test.",
  "Customer reports loss of function after cold soak; diagnostic trouble code stored.",
  "Customer states noise and irregular operation; condition confirmed at idle.",
  "Vehicle towed in with no-start condition; module non-responsive on bus scan.",
  "Customer reports repeat concern after previous visit; component replaced under warranty.",
  "Fluid seepage observed at connection during multipoint inspection.",
  "Function inoperative on driver request; actuator draws out-of-range current.",
  "Display blanks during over-the-air update; unit will not recover after reset.",
];

function vin(seed: () => number, platform: string, modelYear: number, sequence: number) {
  const prefix = WMI[platform] ?? "1BV";
  let body = "";
  for (let i = 0; i < 5; i += 1) {
    body += VIN_CHARS[Math.floor(seed() * VIN_CHARS.length)];
  }
  const check = VIN_CHARS[Math.floor(seed() * VIN_CHARS.length)];
  const year = YEAR_CODE[modelYear] ?? "T";
  const plant = ["A", "F", "K", "N"][Math.floor(seed() * 4)];
  return `${prefix}${body}${check}${year}${plant}${String(100000 + sequence).slice(-6)}`;
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

/**
 * Builds a deterministic set of synthetic warranty claims for a problem record.
 * Replace this generator (or the rows in the claims table) with real claim data.
 */
export function buildClaimsForRecord(record: ProblemRecord, program?: Program): Claim[] {
  const next = rng(record.id);
  const count = Math.max(6, Math.min(18, Math.round(record.claims / 260) + 6));
  const opened = new Date(record.openedAt);
  const modelYear = program?.modelYear ?? 2026;
  const claims: Claim[] = [];

  for (let i = 0; i < count; i += 1) {
    const failure = new Date(opened);
    failure.setDate(failure.getDate() - Math.floor(next() * 150) - 5);
    const reported = new Date(failure);
    reported.setDate(reported.getDate() + Math.floor(next() * 5));
    const repair = new Date(reported);
    repair.setDate(repair.getDate() + Math.floor(next() * 9) + 1);

    const dealer = DEALERS[Math.floor(next() * DEALERS.length)]!;
    const labor = Math.round((90 + next() * 520) * 100) / 100;
    const parts = Math.round((140 + next() * 1450) * 100) / 100;

    claims.push({
      id: `${record.id}-CLM-${String(i + 1).padStart(3, "0")}`,
      claimNumber: `W${record.code.replace("PR-", "")}${String(i + 1).padStart(4, "0")}`,
      vin: vin(next, record.platform, modelYear, i + 1),
      recordId: record.id,
      programId: record.programId,
      vehicleLine: program?.vehicleLine ?? record.platform,
      modelYear,
      failureDate: isoDate(failure),
      reportedDate: isoDate(reported),
      repairDate: isoDate(repair),
      mileage: 1200 + Math.floor(next() * 46000),
      description: `${record.component} — ${record.failureCause}. ${
        SYMPTOMS[Math.floor(next() * SYMPTOMS.length)]
      }`,
      component: record.component,
      failureCode: `${record.code.replace("PR-", "FC")}-${String(10 + Math.floor(next() * 89))}`,
      dealer: dealer.name,
      region: dealer.region,
      laborCost: labor,
      partsCost: parts,
      totalCost: Math.round((labor + parts) * 100) / 100,
      status: STATUSES[Math.floor(next() * STATUSES.length)]!,
    });
  }

  return claims;
}

export function buildAllClaims(records: ProblemRecord[], programs: Program[]): Claim[] {
  return records.flatMap((r) =>
    buildClaimsForRecord(
      r,
      programs.find((p) => p.id === r.programId),
    ),
  );
}
